import type { MutableRefObject } from 'react';
import React, { useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import RecordMenuItem from '@components/RecordMenuItem/index';
import RecordSync from '@components/RecordSync/index';

import {
  ACTIVITY_ID,
  ALLERGY_ID,
  APPOINTMENTS_ID,
  BODY_ID,
  BODY_SCAN_ID,
  CARRIER_STATUS_ID,
  COUGH_SCAN_ID,
  EMERGENCY_VISIT_ID,
  FACE_SCAN_ID,
  FAMILY_HISTORY_ID,
  FITNESS_ID,
  HOSPITALIZATION_ID,
  IMAGING_TESTS_ID,
  IMMUNIZATION_ID,
  LAB_RESULTS_ID,
  MEDICAL_CONDITION_ID,
  MEDICATION_ID,
  SKIN_SCAN_ID,
  SLEEP_ID,
  SURGERY_ID,
  VITAL_SIGN_ID,
} from '@constants/survey-ids';

import { useGetViewedStatusCounts } from '@hooks/api/extension';
import { useSession } from '@hooks/useSession';

import { SideDrawerStyle, SyncWrapper } from './SideDrawer.style';

const historyItems = [
  APPOINTMENTS_ID,
  SURGERY_ID,
  EMERGENCY_VISIT_ID,
  HOSPITALIZATION_ID,
  IMAGING_TESTS_ID,
  CARRIER_STATUS_ID,
] as const;

const healthItems = [
  MEDICAL_CONDITION_ID,
  VITAL_SIGN_ID,
  MEDICATION_ID,
  ALLERGY_ID,
  IMMUNIZATION_ID,
  LAB_RESULTS_ID,
  FAMILY_HISTORY_ID,
] as const;

const fitnessBodyItems = [FITNESS_ID, BODY_ID, SLEEP_ID, ACTIVITY_ID] as const;

const notif_counts = {
  [ALLERGY_ID]: 'allergy',
  [APPOINTMENTS_ID]: 'appointment',
  [SURGERY_ID]: 'surgery',
  [EMERGENCY_VISIT_ID]: 'emergencyVisit',
  [HOSPITALIZATION_ID]: 'hospitalization',
  [IMMUNIZATION_ID]: 'immunization',
  [MEDICAL_CONDITION_ID]: 'medicalCondition',
  [MEDICATION_ID]: 'medication',
  [IMAGING_TESTS_ID]: 'imagingTests',
  [VITAL_SIGN_ID]: 'vitalSign',
  [CARRIER_STATUS_ID]: 'geneticInformation',
  [LAB_RESULTS_ID]: 'labReport',
  [FAMILY_HISTORY_ID]: 'familyHistory',
  [FITNESS_ID]: 'fitness',
  [BODY_ID]: 'body',
  [SLEEP_ID]: 'sleep',
  [ACTIVITY_ID]: 'activity',
  [BODY_SCAN_ID]: 'bodyScan',
  [FACE_SCAN_ID]: 'faceScan',
  [COUGH_SCAN_ID]: 'coughScan',
  [SKIN_SCAN_ID]: 'skinScan',
};

const messages = defineMessages({
  medicalHistory: {
    id: 'sideDrawer.medicalHistory',
    defaultMessage: 'MEDICAL RECORDS',
  },
  fitnessBody: {
    id: 'sideDrawer.fitnessBody',
    defaultMessage: 'WELLNESS METRICS',
  },
  scanHistory: {
    id: 'sideDrawer.scanHistory',
    defaultMessage: 'SCAN RESULTS',
  },
});

interface SideDrawerProps {
  isOpen?: boolean;
  onClose?: (event: MouseEvent | React.MouseEvent) => void;
  btnRef?: MutableRefObject<HTMLButtonElement | null>;
}

function SideDrawer({
  isOpen = false,
  onClose,
  btnRef = undefined,
}: SideDrawerProps) {
  const {
    state: { activeProfile },
  } = useSession();
  const { formatMessage } = useIntl();
  const drawerRef = useRef<HTMLInputElement>(null);
  const { data } = useGetViewedStatusCounts({
    input: {
      patient_id: activeProfile?.patient_id ?? '',
    },
  });
  const notifications = data?.getRecordViewedStatusCounts as Record<
    string,
    any
  >;

  const scanHistoryItems = [
    FACE_SCAN_ID,
    BODY_SCAN_ID,
    COUGH_SCAN_ID,
    SKIN_SCAN_ID,
  ] as const;

  useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (
        isOpen &&
        onClose &&
        !drawerRef.current?.contains(event.target as Node) &&
        !btnRef?.current?.contains(event.target as Node)
      ) {
        onClose(event);
      }
    }

    document.addEventListener('mousedown', handleClick, false);
    return () => document.removeEventListener('mousedown', handleClick, false);
  }, [onClose, isOpen, btnRef]);

  const handleClick = (e: React.MouseEvent) => {
    onClose?.(e);
  };

  return (
    <SideDrawerStyle className={isOpen ? '' : 'hidden'} ref={drawerRef}>
      <SyncWrapper>
        <RecordSync />
      </SyncWrapper>
      <>
        <h3>{formatMessage(messages.scanHistory)}</h3>
        {scanHistoryItems.map((recordType) => {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          const notifCount = notifications?.[notif_counts?.[recordType]];
          return (
            <RecordMenuItem
              key={recordType}
              notifications={notifCount}
              recordType={recordType}
              onClick={handleClick}
            />
          );
        })}
      </>

      <h3>{formatMessage(messages.medicalHistory)}</h3>
      {historyItems.map((recordType) => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        const notifCount = notifications?.[notif_counts?.[recordType]];
        return (
          <RecordMenuItem
            key={recordType}
            notifications={notifCount}
            recordType={recordType}
            onClick={handleClick}
          />
        );
      })}
      {healthItems.map((recordType) => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        const notifCount = notifications?.[notif_counts?.[recordType]];
        return (
          <RecordMenuItem
            key={recordType}
            notifications={notifCount}
            recordType={recordType}
            onClick={handleClick}
          />
        );
      })}

      <h3>{formatMessage(messages.fitnessBody)}</h3>
      {fitnessBodyItems.map((recordType) => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        const notifCount = notifications?.[notif_counts?.[recordType]];
        return (
          <RecordMenuItem
            key={recordType}
            notifications={notifCount}
            recordType={recordType}
            onClick={handleClick}
          />
        );
      })}
    </SideDrawerStyle>
  );
}

export default SideDrawer;
